import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { isAdditionalCustomKey } from '../../custom-validators';

/**
 * Authoring UI for arbitrary custom-named validators on the schema's
 * top level — anything outside JSON Schema's standard keywords and
 * outside the guided forms (masterDataMappings, dateCompare, sameDay,
 * maxTotalDuration, sumEquals).
 *
 * Editor model (mirrors MasterDataMappingsForm — local state with
 * stable UUIDs, blank rows survive edits, sync to parent via emit):
 *
 *   rows: [{ id, key, valueText, valueParseError? }]
 *
 * On save, valueText is parsed as JSON; if it parses, the parsed value
 * is what flows to the schema, otherwise the raw string flows through
 * (matches the polymorphic-string convention used by `x-uioptions` for
 * `dropdown` uitypes elsewhere).
 *
 * Reserved JSON Schema keys are rejected at the input level so the user
 * can't accidentally rename `properties` or `allOf`.
 */

let __rowCounter = 0;
function newRowId() {
	__rowCounter += 1;
	return `cv-add-${Date.now()}-${__rowCounter}`;
}

function valueToText(v) {
	if (typeof v === 'string') return v;
	try {
		return JSON.stringify(v, null, 2);
	} catch (_e) {
		return '';
	}
}

function listToRows(list) {
	if (!Array.isArray(list)) return [];
	return list.map((entry) => ({
		id: newRowId(),
		key: entry?.key ?? '',
		valueText: valueToText(entry?.value),
	}));
}

function rowsToList(rows) {
	const out = [];
	for (const r of rows) {
		const key = (r.key || '').trim();
		if (!key) continue;
		if (!isAdditionalCustomKey(key)) continue; // skip rows targeting reserved keys
		const trimmed = (r.valueText || '').trim();
		let parsed;
		if (trimmed === '') {
			// Blank value: skip — equivalent to "no validator yet"
			continue;
		}
		try {
			parsed = JSON.parse(trimmed);
		} catch (_jsonErr) {
			parsed = r.valueText; // round-trip raw string
		}
		out.push({ key, value: parsed });
	}
	return out.length > 0 ? out : null;
}

export default function AdditionalValidatorsForm({
	value,
	onChange,
	disabled = false,
}) {
	const [rows, setRows] = useState(() => listToRows(value));
	const lastSeenValueRef = useRef(value);

	useEffect(() => {
		if (value === lastSeenValueRef.current) return;
		const ourEmit = rowsToList(rows);
		const sameAsRows = JSON.stringify(ourEmit) === JSON.stringify(value);
		lastSeenValueRef.current = value;
		if (!sameAsRows) {
			setRows(listToRows(value));
		}
	}, [value, rows]);

	const commit = (nextRows) => {
		setRows(nextRows);
		const next = rowsToList(nextRows);
		lastSeenValueRef.current = next;
		onChange(next);
	};

	const update = (id, patch) => {
		commit(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
	};

	const add = () => {
		commit([...rows, { id: newRowId(), key: '', valueText: '' }]);
	};

	const remove = (id) => {
		commit(rows.filter((r) => r.id !== id));
	};

	return (
		<div className="dsv-cv-form" data-testid="cv-add-form">
			<p className="dsv-cv-form__hint">
				Use this only for advanced validation rules that do not have a
				guided form yet. Enter the rule name and its value exactly as the
				backend expects.
			</p>

			{rows.length === 0 && (
				<p className="dsv-cv-form__empty" data-testid="cv-add-empty">
					No additional custom validators yet.
				</p>
			)}

			{rows.map((row) => {
				const trimmedKey = (row.key || '').trim();
				const keyInvalid = trimmedKey !== '' && !isAdditionalCustomKey(trimmedKey);
				let parseError = null;
				const trimmedValue = (row.valueText || '').trim();
				if (trimmedValue !== '') {
					try {
						JSON.parse(trimmedValue);
					} catch (_e) {
						parseError = 'Not valid JSON — will round-trip as a raw string.';
					}
				}
				return (
					<div key={row.id} className="dsv-cv-additional-row" data-testid="cv-add-row">
						<div className="dsv-modal__field-row">
							<div className="dsv-modal__field dsv-cv-additional-row__key">
								<label className="dsv-modal__label">Rule Name</label>
								<input
									className="dsv-modal__input"
									type="text"
									value={row.key}
									disabled={disabled}
									onChange={(e) => update(row.id, { key: e.target.value })}
									placeholder="e.g. maxAttempts"
									aria-invalid={keyInvalid}
								/>
								{keyInvalid && (
									<p className="dsv-form-error" data-testid="cv-add-key-error">
										&quot;{trimmedKey}&quot; is a reserved JSON Schema keyword.
									</p>
								)}
							</div>
							{!disabled && (
								<button
									type="button"
									className="dsv-cv-table__remove"
									onClick={() => remove(row.id)}
									data-testid="cv-add-remove"
									aria-label="Remove validator"
									title="Remove validator"
								>
									×
								</button>
							)}
						</div>
						<div className="dsv-modal__field">
							<label className="dsv-modal__label">Rule Value</label>
							<textarea
								className="dsv-modal__input"
								rows={4}
								value={row.valueText}
								disabled={disabled}
								onChange={(e) => update(row.id, { valueText: e.target.value })}
								placeholder='e.g. { "maxAttempts": 3, "windowSeconds": 60 }'
							/>
							{parseError && (
								<p className="dsv-promote__hint" data-testid="cv-add-value-warning">
									{parseError}
								</p>
							)}
						</div>
					</div>
				);
			})}

			{!disabled && (
				<button
					type="button"
					className="dsv-btn dsv-btn--secondary dsv-cv-form__add"
					onClick={add}
					data-testid="cv-add-add"
				>
					+ Add Custom Validator
				</button>
			)}
		</div>
	);
}

AdditionalValidatorsForm.propTypes = {
	value: PropTypes.array,
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
};
